import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MyHttpClient } from '../app/http-client.service';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  chunkList,
  mergeTicketLists,
  paginateTicketList,
  shouldFetchNextTicketPage,
  TICKET_BOOKING_ID_QUERY_CHUNK_SIZE,
  TICKET_CONVERSATION_COMMENT_LIMIT,
  TICKET_V2_DEFAULT_PAGE_SIZE,
} from './shared/ticket-table.helper';

@Injectable({
  providedIn: 'root'
})
export class UserTicketingService {

  server = environment.server;
  fetchTicketsV2Url = `${this.server}/api/user/tickets/v2`;
  fetchTicketsV3Url = `${this.server}/api/user/tickets/v3`;
  fetchTicketCommentsUrl = `${this.server}/api/user/tickets/v1/comments`

  constructor(private http: MyHttpClient) { }

  fetchUserTicketsV2(userId, userType, query, pageNumber, pageSize = TICKET_V2_DEFAULT_PAGE_SIZE){
    const parsedQuery = this.parseTicketQuery(query);
    const bookingIds = parsedQuery && parsedQuery.booking_id;
    if (Array.isArray(bookingIds)) {
      if (bookingIds.length === 0) {
        return of({ tickets: [] });
      }
      const chunks = chunkList(bookingIds, TICKET_BOOKING_ID_QUERY_CHUNK_SIZE);
      return forkJoin(chunks.map((chunk) =>
        this.fetchAllPagesForQuery(userId, userType, { ...parsedQuery, booking_id: chunk })
      )).pipe(
        map((ticketLists: any[]) => {
          const tickets = mergeTicketLists(ticketLists);
          return {
            tickets: pageSize ? paginateTicketList(tickets, pageNumber, pageSize) : tickets
          };
        }),
        catchError(this.handleError)
      );
    }
    return this.requestTicketsV2(userId, userType, query, pageNumber).pipe(catchError(this.handleError));
  }

  private fetchAllPagesForQuery(userId, userType, queryObj, pageNumber = 1) {
    return this.requestTicketsV2(userId, userType, JSON.stringify(queryObj), pageNumber).pipe(
      switchMap((response: any) => {
        const tickets = (response && response.tickets) || [];
        if (shouldFetchNextTicketPage(tickets)) {
          return this.fetchAllPagesForQuery(userId, userType, queryObj, pageNumber + 1).pipe(
            map((nextTickets) => tickets.concat(nextTickets))
          );
        }
        return of(tickets);
      })
    );
  }

  private requestTicketsV2(userId, userType, query, pageNumber) {
    return this.http.get(`${this.fetchTicketsV2Url}/${userId}?userType=${userType}&query=${encodeURIComponent(query)}&page=${pageNumber}`);
  }

  private parseTicketQuery(query) {
    try {
      return JSON.parse(query);
    } catch (error) {
      return null;
    }
  }

  fetchUserTicketsV3(userId, userType, query, pageNumber, limit = 30){
    return this.http.get(`${this.fetchTicketsV3Url}/${userId}?userType=${userType}&query=${encodeURIComponent(query)}&page=${pageNumber}&limit=${limit}`).pipe(catchError(this.handleError));
  }

  fetchUserTicketsByMobileV3(userId, userType, mobile, pageNumber = 1, limit = 30) {
    const query = JSON.stringify({ mobile });
    return this.fetchUserTicketsV3(userId, userType, query, pageNumber, limit);
  }

  fetchTicketConversation(userId, ticketId) {
    return this.fetchTicketLastComment(userId, ticketId, true, TICKET_CONVERSATION_COMMENT_LIMIT);
  }

  fetchTicketLastComment(userId, ticketId, includePrivate, perPage){
    return this.http.get(`${this.fetchTicketCommentsUrl}/${ticketId}/${userId}?includePrivate=${includePrivate}&perPage=${perPage}`).pipe(catchError(this.handleError));
  }
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    return throwError(error);
  }

}
